/**
 * Noticing that a session died while the app was open, and coming back to
 * where you were.
 *
 * Two things went wrong when a session expired mid-task.
 *
 * The app only checked the session on mount, so an expiry part-way through
 * left every panel failing while the shell carried on as though signed in.
 * There was no prompt, no sign-in screen, nothing that named the cause - the
 * only way to find out was to guess and hard-reload. Behind IAP an
 * unauthenticated request is a redirect to accounts.google.com, which fetch
 * can only report as a network failure, so the panels showed "the API could
 * not be reached" for something that was really "sign in again".
 *
 * And signing in returned to the root. The backend refuses a returnTo in the
 * query string on purpose - it would be an open redirect - so the destination
 * has to be kept here, and it is kept as a same-origin path that is validated
 * again on the way out. An expiry deep inside a packaging run should not cost
 * an operator their place.
 */

const RETURN_KEY = 'poseidon:returnTo';

/** Requests a session re-check. The provider verifies before acting on it. */
export type SessionLossListener = () => void;

const listeners = new Set<SessionLossListener>();

export function onPossibleSessionLoss(listener: SessionLossListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Reports that a request failed in a way a dead session would explain.
 *
 * This is a suspicion, never a verdict: a transient network blip and an
 * expired cookie look identical from here, and signing someone out because
 * the API was rolling would be its own defect. The provider re-reads the
 * session and only then decides.
 */
export function reportPossibleSessionLoss(): void {
  for (const listener of listeners) listener();
}

/** Remembers where to come back to. Same-origin paths only. */
export function rememberReturnPath(path: string): void {
  if (!isSafeReturnPath(path)) return;
  try {
    sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    // Private modes can refuse storage; landing on the root is the fallback.
  }
}

/** Takes the remembered path, clearing it so a later reload does not jump. */
export function takeReturnPath(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return isSafeReturnPath(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * A path this app can navigate to, and nothing else.
 *
 * Rejects anything that could leave this origin: absolute URLs, scheme-
 * relative "//host" forms, and backslash variants some parsers normalise into
 * a host. The backend refuses returnTo for exactly this reason, and storing it
 * client-side does not make the check any less necessary.
 */
export function isSafeReturnPath(path: string | null | undefined): path is string {
  if (!path || typeof path !== 'string') return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  if (path.includes('\\')) return false;
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(path)) return false;
  return true;
}
